import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import BackArrowIcon from "../../assets/backArrow.svg";
import { Spinner } from "../../components/Spinner";
import {
  ACTIVATE_SELLER_COD_RATE_CARD,
  DEACTIVATE_SELLER_COD_RATE_CARD,
  GET_SELLER_COD_RATE_CARDS,
} from "../../utils/ApiUrls";
import { POST } from "../../utils/webService";
// import "./styles.css";

interface IRemittanceOption {
  id: string;
  title: string;
  description: string;
}

interface IRemittancePlan {
  id: string;
  days: string;
  paymentBy: number;
  applicableType: string;
  isDefault: boolean;
  options: IRemittanceOption[];
}

interface ICodRateCardConditions {
  grantType: string;
  absoluteValue: number;
  percentValue: number;
  applicableOn: string;
  applicableType: string;
  remitAfterCodReceived?: boolean;
  paymentOn: string;
  paymentBy: number;
  days: Record<string, boolean>;
  codRefund?: boolean;
}

interface ICodRateCard {
  _id: string;
  rateCardName: string;
  rateCardId: string;
  gstPercent: number;
  conditions: ICodRateCardConditions;
  isActive: boolean;
  isDeleted: boolean;
  currentActiveCodRateCardId?: boolean;
  defaultCodRateCard?: boolean;
}

const mapRateCardToPlan = (rateCard: ICodRateCard): IRemittancePlan => {
  const { paymentBy, paymentOn, percentValue, absoluteValue, applicableType } =
    rateCard.conditions;
  const daysLabel =
    paymentOn === "DELIVERY" ? `D+${paymentBy}` : `P+${paymentBy}`;

  return {
    id: rateCard.rateCardId,
    days: daysLabel,
    paymentBy,
    applicableType,
    isDefault: rateCard.defaultCodRateCard === true,
    options: [
      {
        id: `${rateCard.rateCardId}-percent`,
        title: `${percentValue}%`,
        description: `Transaction Charges of COD Amount (${rateCard.gstPercent}% GST Included)`,
      },
      {
        id: `${rateCard.rateCardId}-flat`,
        title: `flat \u20B9${absoluteValue}`,
        description: `Transaction Charges of COD Amount (${rateCard.gstPercent}% GST Included)`,
      },
    ],
  };
};

const EarlyCodRemittance = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<IRemittancePlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [selectedOptionId, setSelectedOptionId] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(true);
  const [activatedPlanId, setActivatedPlanId] = useState("");
  const [activatedOptionId, setActivatedOptionId] = useState("");
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchCodRateCards = async (showPageLoader = false) => {
    try {
      if (showPageLoader) {
        setLoading(true);
      }

      const { data: response }: any = await POST(GET_SELLER_COD_RATE_CARDS, {});

      if (response?.success) {
        const rateCards: ICodRateCard[] = response?.data || [];
        const activeRateCard = rateCards.find(
          (rateCard) => rateCard.currentActiveCodRateCardId === true,
        );

        const mappedPlans = rateCards
          .filter(
            (rateCard: ICodRateCard) =>
              rateCard.isActive && !rateCard.isDeleted,
          )
          .map(mapRateCardToPlan)
          .sort(
            (a: IRemittancePlan, b: IRemittancePlan) =>
              a.paymentBy - b.paymentBy,
          );

        setPlans(mappedPlans);

        if (mappedPlans.length > 0) {
          const activePlanId = activeRateCard?.rateCardId || "";
          const activePlan = mappedPlans.find(
            (plan) => plan.id === activePlanId,
          );
          const defaultPlan = activePlan || mappedPlans[0];

          setSelectedPlanId(defaultPlan.id);
          setSelectedOptionId(defaultPlan.options[0]?.id || "");

          if (activePlan) {
            setActivatedPlanId(activePlan.id);
            setActivatedOptionId(activePlan.options[0]?.id || "");
            setAcceptedTerms(true);
          } else {
            setActivatedPlanId("");
            setActivatedOptionId("");
          }
        }
      } else {
        toast.error(response?.message || "Failed to fetch COD rate cards");
      }
    } catch {
      toast.error("Failed to fetch COD rate cards");
    } finally {
      if (showPageLoader) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchCodRateCards(true);
  }, []);

  const selectPlan = (plan: IRemittancePlan) => {
    if (selectedPlanId !== plan.id) {
      setSelectedOptionId(activatedPlanId === plan.id ? activatedOptionId : "");
      setAcceptedTerms(activatedPlanId === plan.id);
    }

    setSelectedPlanId(plan.id);
  };

  const handleTermsChange = (plan: IRemittancePlan) => {
    if (activatedPlanId === plan.id) return;

    if (selectedPlanId !== plan.id) {
      setSelectedPlanId(plan.id);
      setSelectedOptionId("");
      setAcceptedTerms(true);
      return;
    }

    setAcceptedTerms((prev) => !prev);
  };

  const handleActivate = async (plan: IRemittancePlan) => {
    if (selectedPlanId !== plan.id || !acceptedTerms || submitting) {
      return;
    }

    try {
      setSubmitting(true);
      const { data: response }: any = await POST(
        ACTIVATE_SELLER_COD_RATE_CARD,
        {
          codRateCardId: plan.id,
        },
      );

      if (response?.success) {
        toast.success(response?.message || "Plan activated successfully");
        await fetchCodRateCards();
      } else {
        toast.error(response?.message || "Failed to activate plan");
      }
    } catch {
      toast.error("Failed to activate plan");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async (plan: IRemittancePlan) => {
    if (activatedPlanId !== plan.id || submitting) {
      return;
    }

    try {
      setSubmitting(true);
      const { data: response }: any = await POST(
        DEACTIVATE_SELLER_COD_RATE_CARD,
        {
          codRateCardId: plan.id,
        },
      );

      if (response?.success) {
        toast.success(response?.message || "Plan deactivated successfully");
        await fetchCodRateCards();
      } else {
        toast.error(response?.message || "Failed to deactivate plan");
      }
    } catch {
      toast.error("Failed to deactivate plan");
    } finally {
      setSubmitting(false);
    }
  };

  const getChargeInfoText = (applicableType: string) => {
    if (applicableType === "HIGHER") {
      return "Flow COD charge = Higher of Flat Fee or % Fee*.";
    }
    if (applicableType === "LOWER") {
      return "Flow COD charge = Lower of Flat Fee or % Fee*.";
    }
    return "Flow COD charge applies as per the selected rate card.";
  };

  return (
    <main className="min-h-full bg-white px-5 pb-8 pt-4 font-Open lg:px-6">
      <header>
        <div className="hidden items-center gap-2 text-sm text-[#777777] lg:flex">
          <button
            type="button"
            className="text-[#1C1C1C] hover:underline"
            onClick={() => navigate("/dashboard/overview")}
          >
            Home
          </button>
          <span>/</span>
          <span className="font-semibold text-[#1C1C1C]">
            Early COD Remittance
          </span>
        </div>

        <div className="mt-1 flex items-center gap-3">
          <button
            type="button"
            className="flex h-8 w-6 items-center justify-start"
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            <img src={BackArrowIcon} alt="" className="h-4 w-3" />
          </button>
          <h1 className="text-[24px] font-semibold leading-8 text-[#1C1C1C] lg:text-[28px]">
            Early COD Remittance
          </h1>
        </div>
      </header>

      <section className="mt-2 w-full px-5">
        <p className="text-sm font-normal leading-5 text-[#7A8796]">
          Get guaranteed remittance from the shipment delivered date.
        </p>

        <div className="mt-8 rounded-md border border-[#FFE0B2] bg-[#FFF8EE] px-4 py-3 text-sm leading-5 text-[#8A5A00]">
          <span className="font-semibold">Note:</span> Once a COD rate card is
          activated or changed on selection, it cannot be deactivated or
          switched to a new COD rate card for 30 days.
        </div>

        <h2 className="mt-7 text-lg font-semibold leading-6 text-[#25364A]">
          Choose a plan
        </h2>

        {loading ? (
          <div className="mt-10 flex justify-center">
            <Spinner />
          </div>
        ) : plans.length === 0 ? (
          <p className="mt-10 text-sm text-[#7A8796]">
            No COD rate cards available at the moment.
          </p>
        ) : (
          <div className="relative w-full">
            <div
              className="
              grid
              auto-cols-[calc((100%-60px)/3.25)]
              grid-flow-col
              gap-5
              overflow-x-auto
              overscroll-x-contain
              scroll-smooth
              [scrollbar-width:none]
              [-ms-overflow-style:none]
              [&::-webkit-scrollbar]:hidden
              pt-4
              py-1
              pb-3
              lg:auto-cols-[calc((100%-60px)/3.25)]
              max-lg:auto-cols-[minmax(280px,88vw)]
              max-lg:gap-4
              max-lg:pr-5
            "
            >
              {plans.map((plan) => {
                const isSelectedPlan = selectedPlanId === plan.id;
                const isActivatedPlan = activatedPlanId === plan.id;
                const canEditPlan = !isActivatedPlan;
                const isReadyToActivate =
                  canEditPlan && isSelectedPlan && acceptedTerms;
                const canClickActivate =
                  !submitting && (isActivatedPlan || isReadyToActivate);

                return (
                  <article
                    key={plan.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      selectPlan(plan);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        selectPlan(plan);
                      }
                    }}
                    className={`relative flex min-h-[372px] flex-col rounded-lg border bg-white px-4 pb-4 pt-5 transition-all ${
                      isActivatedPlan || isSelectedPlan
                        ? "cursor-pointer border-[#2196F3] opacity-100"
                        : "cursor-pointer border-[#E1E7EC] opacity-60 hover:opacity-100"
                    }`}
                  >
                    {isActivatedPlan && (
                      <span className="absolute left-1/2 top-[-12px] -translate-x-1/2 rounded bg-[#0FA958] px-3 py-1 text-xs font-medium leading-4 text-white">
                        Current Active Plan
                      </span>
                    )}

                    {plan.isDefault && (
                      <span className="absolute right-3 top-3 rounded border border-[#FFE0B2] bg-[#FFF8EE] px-2 py-0.5 text-[10px] font-medium leading-4 text-[#C77700]">
                        Default Assigned
                      </span>
                    )}

                    <div className="text-center">
                      <p className="text-[10px] font-medium uppercase leading-4 text-[#7C8A9B]">
                        GET COD AMOUNT WITHIN
                      </p>
                      <h3 className="mt-1 text-[36px] font-semibold leading-[42px] text-[#2C3E50]">
                        {plan.days}
                      </h3>
                      <p className="mt-1 text-xs font-normal leading-4 text-[#7C8A9B]">
                        from the date of delivery
                      </p>
                    </div>

                    <div className="mt-4">
                      {plan.options.map((option) => (
                        <div
                          key={option.id}
                          className="mt-2 flex min-h-[80px] w-full rounded-md border border-[#E0E6ED] bg-[#FFFFFF] px-3 py-3"
                        >
                          <div>
                            <div className="text-base font-semibold leading-5 text-[#2C3E50]">
                              {option.title}
                            </div>
                            <div className="mt-2 text-[13px] font-normal leading-[18px] text-[#6F7F8F]">
                              {option.description}
                            </div>
                          </div>
                        </div>
                      ))}
                      <div className="mt-2 flex min-h-[20px] w-full rounded-md border border-[#2196F3] bg-[#F8FCFF] px-3 py-3 text-xs font-bold text-[#494949]">
                        {getChargeInfoText(plan.applicableType)}
                      </div>
                    </div>

                    <div className="mt-4 flex items-center gap-1.5 text-[11px] leading-4 text-[#7C8A9B]">
                      <button
                        type="button"
                        disabled={!canEditPlan}
                        role="checkbox"
                        aria-checked={isSelectedPlan && acceptedTerms}
                        className={`flex items-center gap-2 focus:outline-none ${
                          canEditPlan ? "cursor-pointer" : "cursor-default"
                        }`}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleTermsChange(plan);
                        }}
                      >
                        <span
                          className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border text-[10px] font-bold ${
                            isSelectedPlan && acceptedTerms
                              ? "border-[#2196F3] bg-[#2196F3] text-white"
                              : "border-[#CBD5DF] bg-white text-white"
                          }`}
                        >
                          {isSelectedPlan && acceptedTerms ? "\u2713" : ""}
                        </span>
                        <span>I agree to</span>
                      </button>
                      <button
                        type="button"
                        className="font-medium text-[#008CFF] focus:outline-none"
                        onClick={(event) => {
                          event.stopPropagation();
                          setIsTermsModalOpen(true);
                        }}
                      >
                        Terms and Conditions
                      </button>
                    </div>

                    <button
                      type="button"
                      disabled={!canClickActivate}
                      className={`mt-2 h-9 w-full rounded-md text-sm font-semibold leading-5 transition-colors ${
                        canClickActivate
                          ? isActivatedPlan
                            ? "bg-[#E53935] text-white hover:bg-[#C62828]"
                            : "bg-[#2196F3] text-white hover:bg-[#1688E6]"
                          : "cursor-not-allowed bg-[#F0F3F6] text-[#AEB8C2]"
                      }`}
                      onClick={(event) => {
                        event.stopPropagation();
                        if (isActivatedPlan) {
                          handleDeactivate(plan);
                          return;
                        }
                        handleActivate(plan);
                      }}
                    >
                      {submitting && isActivatedPlan
                        ? "Deactivating..."
                        : submitting && isSelectedPlan
                          ? "Activating..."
                          : isActivatedPlan
                            ? "Deactivate"
                            : "Activate"}
                    </button>
                  </article>
                );
              })}
            </div>
          </div>
        )}
      </section>

      {isTermsModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="early-cod-terms-title"
          onClick={() => setIsTermsModalOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <h3
              id="early-cod-terms-title"
              className="text-lg font-semibold leading-6 text-[#25364A]"
            >
              Terms and Conditions
            </h3>
            <p className="mt-3 text-sm leading-5 text-[#6F7F8F]">
              This is temporary sample content for Early COD Remittance. The
              selected plan may apply only to eligible COD shipments, and the
              remittance cycle can vary based on shipment status, courier
              confirmation, wallet balance, and internal verification.
            </p>
            <ul className="mt-4 list-disc space-y-2 pl-5 text-sm leading-5 text-[#6F7F8F]">
              <li>
                Charges are calculated on the COD amount for each shipment.
              </li>
              <li>Only one remittance plan can stay active at a time.</li>
              <li>
                The active plan can be changed after selecting a new plan.
              </li>
            </ul>
            <button
              type="button"
              className="mt-5 h-9 w-full rounded-md bg-[#2196F3] text-sm font-semibold text-white hover:bg-[#1688E6]"
              onClick={() => setIsTermsModalOpen(false)}
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </main>
  );
};

export default EarlyCodRemittance;
